/**
 * Koszty — księga wydatków pracowni.
 *
 * Widok odpowiada na dwa pytania i nic poza nimi:
 *
 *   1. Ile wydajemy w tym okresie i na co?
 *   2. Co z tego obciąża konkretny produkt, a co całą pracownię?
 *
 * Rozróżnienie w drugim pytaniu jest powodem, dla którego ta strona w ogóle
 * powstała. Koszt bez produktu (księgowość, sprzęt, domena firmowa) nie ma jak
 * trafić do marży żadnego produktu, a bez niego „marża portfolio" nie jest
 * marżą — ADR 0008.
 *
 * Kwoty w tabeli są dwie i to nie jest nadmiar: „kwota" to tyle, ile wyszło
 * z konta, a „dziennie" to tyle, ile z tej kwoty przypada na jeden dzień
 * okresu. Marża liczy się z tej drugiej — roczna faktura za domenę
 * zaksięgowana w jednym dniu robiłaby jednodniowy krater i 364 dni
 * fikcyjnego zysku.
 */

import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { RangePicker } from "../components/RangePicker";
import { Card, Chip, Empty, ErrorState, Headline, Level, Loading } from "../components/ui";
import { ApiError, api } from "../lib/api";
import { date, dateShort, LABELS, money, moneyExact } from "../lib/format";
import type { Cost, CostList, ProductBrief } from "../lib/types";

/** Slug, którym API rozumie „to nie należy do żadnego produktu". */
const FIRMA = "firma";

const KATEGORIE = [
  "hosting",
  "domain",
  "tooling",
  "ads",
  "payment_fees",
  "contractor",
  "accounting",
  "equipment",
  "legal",
  "other",
] as const;

type Form = {
  product: string;
  category: string;
  description: string;
  amount: string;
  currency: string;
  period_start: string;
  period_end: string;
  recurring: boolean;
};

function miesiac(): { start: string; end: string } {
  const teraz = new Date();
  const pierwszy = new Date(teraz.getFullYear(), teraz.getMonth(), 1);
  const ostatni = new Date(teraz.getFullYear(), teraz.getMonth() + 1, 0);
  return { start: iso(pierwszy), end: iso(ostatni) };
}

function iso(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(
    value.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * „1 234,56" → 123456 groszy.
 *
 * Człowiek wpisuje złote, API przyjmuje wyłącznie grosze (pieniądze są
 * liczbami całkowitymi). Zamiana jest tutaj, bo tylko interfejs wie, że
 * w polu stoi przecinek dziesiętny.
 */
function naGrosze(raw: string): number | null {
  const oczyszczone = raw.replace(/\s/g, "").replace(",", ".");
  if (!oczyszczone || !/^\d*\.?\d*$/.test(oczyszczone)) return null;
  const liczba = Number(oczyszczone);
  if (!Number.isFinite(liczba)) return null;
  return Math.round(liczba * 100);
}

/**
 * „19.06 — 28.08" albo „19.06.2026 — 18.06.2027".
 *
 * Rok dochodzi dopiero wtedy, gdy okres go przekracza. Roczna faktura
 * wyświetlona jako „19.06 — 18.06" wygląda na okres wpisany od końca, a jest
 * poprawna — i dokładnie na tym wykłada się czytanie tabeli kosztów.
 */
function okres(od: string, do_: string): string {
  const tenSamRok = new Date(od).getFullYear() === new Date(do_).getFullYear();
  return tenSamRok ? `${dateShort(od)} — ${dateShort(do_)}` : `${date(od)} — ${date(do_)}`;
}

function zGroszy(minor: number): string {
  return (minor / 100).toFixed(2).replace(".", ",");
}

function pustyForm(currency: string): Form {
  const okres = miesiac();
  return {
    product: FIRMA,
    category: "hosting",
    description: "",
    amount: "",
    currency,
    period_start: okres.start,
    period_end: okres.end,
    recurring: false,
  };
}

export function CostsPage() {
  const queryClient = useQueryClient();
  const [range, setRange] = useState("30d");
  const [editing, setEditing] = useState<Cost | null>(null);

  // Wybrany produkt siedzi w adresie, nie w stanie komponentu: karta produktu
  // prowadzi wprost do jego kosztów, a odświeżenie strony nie gubi filtru.
  // `replace` — przeklikiwanie zakresów nie zaśmieca historii przeglądarki.
  const [params, setParams] = useSearchParams();
  const scope = params.get("produkt") ?? "";
  const setScope = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("produkt", value);
    else next.delete("produkt");
    setParams(next, { replace: true });
  };

  const products = useQuery({
    queryKey: ["products", "wszystkie"],
    queryFn: () => api.get<{ products: ProductBrief[] }>("/products"),
  });

  const filtr = scope ? `&product=${encodeURIComponent(scope)}` : "";
  const costs = useQuery({
    queryKey: ["costs", range, scope],
    queryFn: () => api.get<CostList>(`/costs?range=${range}${filtr}`),
  });

  const currency = costs.data?.reporting_currency ?? "PLN";
  const productList = products.data?.products ?? [];

  const odswiez = () => {
    void queryClient.invalidateQueries({ queryKey: ["costs"] });
    // Koszt zmienia marżę, więc przegląd portfolio i karty produktów są
    // nieaktualne w tej samej sekundzie, w której wpis został zapisany.
    void queryClient.invalidateQueries({ queryKey: ["portfolio"] });
    void queryClient.invalidateQueries({ queryKey: ["product"] });
  };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Portfolio</p>
          <h1>Koszty</h1>
          <p className="page-head__sub">
            Co pracownia wydaje i na czyje konto to idzie. Koszt bez produktu
            obciąża marżę portfolio, a nie marżę któregokolwiek produktu.
          </p>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <div className="stack">
        <ScopePicker
          scope={scope}
          onChange={setScope}
          products={productList}
          costs={costs.data?.costs ?? []}
        />

        {costs.isPending ? (
          <Loading label="Wczytuję koszty…" />
        ) : costs.isError ? (
          <ErrorState error={costs.error} />
        ) : costs.data ? (
          <>
            <Headline
              label={`Koszty okresu · ${currency}`}
              value={money(costs.data.summary.total_minor, currency)}
              meta={
                <span>
                  {date(costs.data.period.start)} — {date(costs.data.period.end)}
                </span>
              }
              support={[
                {
                  label: "Produkty",
                  value: money(costs.data.summary.products_minor, currency, { compact: true }),
                  foot: "wchodzi do marży produktu",
                  title:
                    "Koszty przypisane do produktów, w części przypadającej na ten okres. " +
                    "Księga pokazuje wszystkie wpisy pracowni — także produktów archiwalnych " +
                    "i odciętych przełącznikiem demo, których marża portfolio nie liczy. " +
                    "Dlatego ta liczba bywa wyższa niż kafelek „Koszty produktów” w przeglądzie.",
                },
                {
                  label: "Cała pracownia",
                  value: money(costs.data.summary.company_minor, currency, { compact: true }),
                  foot: "wchodzi do marży portfolio",
                  title: "Koszty bez przypisania: księgowość, sprzęt, domena firmowa",
                },
                {
                  label: "Wpisów w okresie",
                  value: String(costs.data.costs.length),
                  foot: costs.data.costs.some((c) => c.recurring) ? "w tym cykliczne" : " ",
                },
              ]}
            />

            {costs.data.summary.warnings.length > 0 && (
              <p className="gap-note" role="alert">
                {costs.data.summary.warnings.join(" ")}
              </p>
            )}

            <CostForm
              key={editing?.id ?? "nowy"}
              editing={editing}
              products={productList}
              reportingCurrency={currency}
              onDone={() => {
                setEditing(null);
                odswiez();
              }}
              onCancel={() => setEditing(null)}
            />

            <Level
              title="Wpisy"
              question="Który wydatek jest czyj i ile z niego przypada na ten okres?"
            >
              <Card flush>
                <CostTable rows={costs.data.costs} onEdit={setEditing} onDeleted={odswiez} />
              </Card>
            </Level>
          </>
        ) : null}
      </div>
    </>
  );
}

/* ── Zakres ───────────────────────────────────────────────────────────────── */

function ScopePicker({
  scope,
  onChange,
  products,
  costs,
}: {
  scope: string;
  onChange: (value: string) => void;
  products: ProductBrief[];
  costs: Cost[];
}) {
  const liczba = (predykat: (c: Cost) => boolean) => costs.filter(predykat).length;

  return (
    <section className="scope" aria-labelledby="koszty-zakres">
      <div className="scope__row">
        <p className="scope__eyebrow" id="koszty-zakres">
          Przypisanie
        </p>
        <div className="scope__options" role="group" aria-labelledby="koszty-zakres">
          <button
            type="button"
            className="scope__option"
            aria-pressed={scope === ""}
            onClick={() => onChange("")}
          >
            Wszystko
            {scope === "" && <span className="scope__count mono">{costs.length}</span>}
          </button>
          <button
            type="button"
            className="scope__option"
            aria-pressed={scope === FIRMA}
            onClick={() => onChange(FIRMA)}
          >
            Cała pracownia
            {scope === FIRMA && (
              <span className="scope__count mono">{liczba((c) => c.product_id === null)}</span>
            )}
          </button>
          {products.map((product) => (
            <button
              key={product.slug}
              type="button"
              className="scope__option"
              aria-pressed={scope === product.slug}
              style={{ ["--accent" as string]: product.accent_color }}
              onClick={() => onChange(product.slug)}
            >
              {product.name}
              {scope === product.slug && (
                <span className="scope__count mono">
                  {liczba((c) => c.product_slug === product.slug)}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <p className="scope__sentence">
        {scope === "" ? (
          <>
            Widzisz <strong>wszystkie koszty</strong> — produktowe i firmowe razem.
          </>
        ) : scope === FIRMA ? (
          <>
            Widzisz <strong>koszty całej pracowni</strong>. Nie należą do żadnego produktu
            i&nbsp;nie zmieniają jego rentowności.
          </>
        ) : (
          <>
            Widzisz <strong>koszty jednego produktu</strong>. Te same liczby siedzą w&nbsp;jego
            marży.
          </>
        )}
      </p>
    </section>
  );
}

/* ── Formularz ────────────────────────────────────────────────────────────── */

function CostForm({
  editing,
  products,
  reportingCurrency,
  onDone,
  onCancel,
}: {
  editing: Cost | null;
  products: ProductBrief[];
  reportingCurrency: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [open, setOpen] = useState(Boolean(editing));
  const [form, setForm] = useState<Form>(() =>
    editing
      ? {
          product: editing.product_slug ?? FIRMA,
          category: editing.category,
          description: editing.description ?? "",
          amount: zGroszy(editing.amount_minor),
          currency: editing.currency,
          period_start: editing.period_start,
          period_end: editing.period_end,
          recurring: editing.recurring,
        }
      : pustyForm(reportingCurrency),
  );

  const grosze = naGrosze(form.amount);
  // Rozkład na dni jest wzorem ze słownika, więc liczy go backend (Z5) — ten
  // sam model, który potem liczy marżę. Podgląd w przeglądarce dzielił kwotę
  // po swojemu i liczył dni z `new Date("RRRR-MM-DD")`, czyli z północy UTC.
  const podglad = useQuery({
    queryKey: ["costs", "preview", grosze, form.period_start, form.period_end],
    queryFn: ({ signal }) =>
      api.get<{ days: number; daily_amount_minor: number }>(
        `/costs/preview?${new URLSearchParams({
          amount_minor: String(grosze),
          period_start: form.period_start,
          period_end: form.period_end,
        })}`,
        signal,
      ),
    enabled: grosze !== null && grosze > 0 && Boolean(form.period_start && form.period_end),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const zapisz = useMutation({
    mutationFn: (allowOverlap: boolean) => {
      const body = {
        product: form.product === FIRMA ? null : form.product,
        category: form.category,
        description: form.description.trim(),
        amount_minor: grosze,
        currency: form.currency.toUpperCase(),
        period_start: form.period_start,
        period_end: form.period_end,
        recurring: form.recurring,
        allow_overlap: allowOverlap,
      };
      return editing
        ? api.patch<{ cost: Cost }>(`/costs/${editing.id}`, body)
        : api.post<{ cost: Cost }>("/costs", body);
    },
    onSuccess: () => {
      if (!editing) setForm(pustyForm(reportingCurrency));
      onDone();
    },
  });

  const konflikt = zapisz.error instanceof ApiError && zapisz.error.code === "cost_overlap";
  const set =
    <K extends keyof Form>(field: K) =>
    (value: Form[K]) =>
      setForm((current) => ({ ...current, [field]: value }));

  if (!editing && !open) {
    return (
      <Card
        title="Nowy koszt"
        note="Kwota za cały okres — HQ rozłoży ją na dni"
        action={
          <button type="button" className="btn" onClick={() => setOpen(true)}>
            Dodaj koszt
          </button>
        }
      >
        <p className="muted">
          Wpisz to, co realnie wychodzi z konta: hosting, domenę, księgowość, sprzęt,
          podwykonawcę. Koszt cykliczny wpisuje się osobnym wierszem na każdy okres —
          dzięki temu podwyżka ceny hostingu nie przepisuje historii wstecz.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title={editing ? "Poprawka wpisu" : "Nowy koszt"}
      note={
        editing
          ? `${date(editing.period_start)} — ${date(editing.period_end)}`
          : "Kwota za cały okres — HQ rozłoży ją na dni"
      }
      action={
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() => {
            if (editing) onCancel();
            else setOpen(false);
          }}
        >
          {editing ? "Anuluj" : "Zwiń"}
        </button>
      }
    >
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          zapisz.mutate(false);
        }}
      >
        <label>
          <span>Czyj to koszt</span>
          <select value={form.product} onChange={(event) => set("product")(event.target.value)}>
            <option value={FIRMA}>Cała pracownia (bez produktu)</option>
            {products.map((product) => (
              <option key={product.slug} value={product.slug}>
                {product.name}
              </option>
            ))}
          </select>
          <small>
            {form.product === FIRMA
              ? "Obciąży marżę portfolio. Żaden produkt nie zmieni przez to rentowności."
              : "Obciąży marżę tego produktu i wejdzie do odpowiedzi „czy on się spina”."}
          </small>
        </label>

        <label>
          <span>Kategoria</span>
          <select value={form.category} onChange={(event) => set("category")(event.target.value)}>
            {KATEGORIE.map((klucz) => (
              <option key={klucz} value={klucz}>
                {LABELS.costCategory[klucz] ?? klucz}
              </option>
            ))}
          </select>
          {form.category === "ads" && (
            <small>
              Reklama wpisana tutaj wchodzi do marży, ale <strong>nie do CAC</strong>. Wydatki
              reklamowe do CAC idą przez <code>POST /metrics/ads</code>.
            </small>
          )}
        </label>

        <label className="form-grid__wide">
          <span>Czego dotyczy</span>
          <input
            placeholder="VPS + backupy, faktura brutto"
            value={form.description}
            onChange={(event) => set("description")(event.target.value)}
          />
          <small>Zapisz tu podstawę kwoty: netto czy brutto. Za pół roku nikt tego nie odtworzy.</small>
        </label>

        <label>
          <span>Kwota za cały okres</span>
          <input
            required
            inputMode="decimal"
            placeholder="120,00"
            value={form.amount}
            onChange={(event) => set("amount")(event.target.value)}
          />
          {grosze !== null && grosze > 0 && podglad.data ? (
            <small>
              {moneyExact(grosze, form.currency)} przez {podglad.data.days}{" "}
              {podglad.data.days === 1 ? "dzień" : "dni"} ={" "}
              <strong>{moneyExact(podglad.data.daily_amount_minor, form.currency)}</strong> dziennie
            </small>
          ) : (
            <small>Tyle, ile wyszło z konta — nie miesięcznie, tylko za wskazany okres.</small>
          )}
        </label>

        <label>
          <span>Waluta</span>
          <input
            required
            maxLength={3}
            value={form.currency}
            onChange={(event) => set("currency")(event.target.value.toUpperCase())}
          />
          {form.currency.toUpperCase() !== reportingCurrency && (
            <small>
              Waluta obca wymaga kursu w bazie — bez niego koszt wypadłby z marży po cichu.
            </small>
          )}
        </label>

        <label>
          <span>Okres od</span>
          <input
            required
            type="date"
            value={form.period_start}
            onChange={(event) => set("period_start")(event.target.value)}
          />
        </label>

        <label>
          <span>Okres do</span>
          <input
            required
            type="date"
            value={form.period_end}
            onChange={(event) => set("period_end")(event.target.value)}
          />
        </label>

        <label className="checkbox form-grid__wide">
          <input
            type="checkbox"
            checked={form.recurring}
            onChange={(event) => set("recurring")(event.target.checked)}
          />
          <span>
            Koszt powtarzalny. Znacznik jest opisem, nie automatem — kolejny miesiąc wpisuje
            się osobnym wierszem, żeby zmiana ceny nie przepisała historii.
          </span>
        </label>

        {zapisz.isError && (
          <div className="form-error form-grid__wide" role="alert">
            <p>{zapisz.error instanceof Error ? zapisz.error.message : "Nie udało się zapisać"}</p>
            {konflikt && (
              <button
                type="button"
                className="btn btn--quiet"
                style={{ marginTop: "var(--space-2)" }}
                disabled={zapisz.isPending}
                onClick={() => zapisz.mutate(true)}
              >
                To osobna faktura — dodaj mimo to
              </button>
            )}
          </div>
        )}

        <div className="form-grid__wide">
          <button
            className="btn btn--primary"
            type="submit"
            disabled={zapisz.isPending || grosze === null || grosze <= 0}
          >
            {zapisz.isPending ? "Zapisuję…" : editing ? "Zapisz zmiany" : "Dodaj koszt"}
          </button>
        </div>
      </form>
    </Card>
  );
}

/* ── Tabela ───────────────────────────────────────────────────────────────── */

function CostTable({
  rows,
  onEdit,
  onDeleted,
}: {
  rows: Cost[];
  onEdit: (cost: Cost) => void;
  onDeleted: () => void;
}) {
  if (!rows.length) {
    return (
      <Empty
        title="Żadnego kosztu w tym okresie"
        hint="Bez kosztów marża jest równa przychodowi, a to nieprawda przy pierwszym rachunku za hosting."
      />
    );
  }

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>Okres</th>
            <th>Czyj</th>
            <th>Kategoria</th>
            <th>Czego dotyczy</th>
            <th className="num">Kwota</th>
            <th className="num">Dziennie</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((cost) => (
            <CostRow key={cost.id} cost={cost} onEdit={onEdit} onDeleted={onDeleted} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostRow({
  cost,
  onEdit,
  onDeleted,
}: {
  cost: Cost;
  onEdit: (cost: Cost) => void;
  onDeleted: () => void;
}) {
  // Kasowanie jest nieodwracalne i zmienia marżę, więc ma dwa kroki. Okno
  // dialogowe byłoby trzecim elementem interfejsu do utrzymania; ten sam
  // przycisk w dwóch stanach wystarcza.
  const [potwierdza, setPotwierdza] = useState(false);

  const usun = useMutation({
    mutationFn: () => api.del(`/costs/${cost.id}`),
    onSuccess: onDeleted,
  });

  return (
    <tr>
      <td className="mono">
        {okres(cost.period_start, cost.period_end)}
        <span className="dim"> · {cost.days} dni</span>
      </td>
      <td>
        {cost.product_id === null ? (
          <Chip tone="info" title="Koszt bez przypisania — obciąża marżę portfolio">
            pracownia
          </Chip>
        ) : (
          (cost.product_name ?? (
            <Chip
              tone="warn"
              title="Wpis wskazuje produkt, którego już nie ma. Nie wchodzi do żadnej marży — do skasowania."
            >
              produkt usunięty
            </Chip>
          ))
        )}
      </td>
      <td>
        {LABELS.costCategory[cost.category] ?? cost.category}
        {cost.recurring && (
          <span className="tag-cycle" title="Koszt powtarzalny">
            cykl
          </span>
        )}
      </td>
      <td style={{ whiteSpace: "normal" }}>{cost.description ?? <span className="dim">—</span>}</td>
      <td className="num">{moneyExact(cost.amount_minor, cost.currency)}</td>
      <td
        className="num dim"
        title={`Tyle z tej kwoty przypada na jeden dzień okresu — w walucie ${cost.currency}`}
      >
        {moneyExact(cost.daily_amount_minor, cost.currency)}
      </td>
      <td>
        <div className="costs__actions">
          <button type="button" className="btn btn--quiet" onClick={() => onEdit(cost)}>
            Popraw
          </button>
          {potwierdza ? (
            <button
              type="button"
              className="btn btn--danger"
              disabled={usun.isPending}
              onClick={() => usun.mutate()}
            >
              {usun.isPending ? "Usuwam…" : "Na pewno usuń"}
            </button>
          ) : (
            <button type="button" className="btn btn--quiet" onClick={() => setPotwierdza(true)}>
              Usuń
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
